<script setup lang="ts">
import type { SubmitEventPromise } from 'vuetify';
import type { ReadonlyDeep } from 'type-fest';
import type {
  InitialData as NewQueryDialogFormInitialData,
  ValidData as NewQueryDialogFormValidData,
} from '@/components/dialogForms/NewQueryDialogForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, useTemplateRef } from 'vue';

import JobsBasicList from '@/components/basicLists/JobsBasicList.vue';
import KBtn from '@/components/btns/KBtn.vue';
import NewQueryDialogForm from '@/components/dialogForms/NewQueryDialogForm.vue';
import ReportGenerateDialogHeader from '@/components/headers/ReportGenerateDialogHeader.vue';
import PageDialogWrapper from '@/components/wrappers/PageDialogWrapper.vue';

export interface Props {
  isConfirmLoading?: boolean;
  job?: ReadonlyDeep<
    Pick<
      openapi.JobDetailedOpenapi,
      'rockhopper_id' | 'rockhopper_date' | 'rockhopper_key'
    >
  >;
}
export type DefProps = typeof props;
const props = withDefaults(defineProps<Props>(), {
  isConfirmLoading: false,
  job: undefined,
});
const emit = defineEmits<{
  cancelBtnClick: [event: Event];
  confirmBtnClick: [
    event: SubmitEventPromise | null | undefined,
    formData: NewQueryDialogFormValidData,
    props: DefProps,
  ];
}>();

function handleCancelClick(event: Event): void {
  // also it should be wrapped with <v-dialog :persistent="isConfirmLoading"
  if (props.isConfirmLoading) return;
  emit('cancelBtnClick', event);
}

function handleSubmit(
  event: SubmitEventPromise | null | undefined,
  formData: NewQueryDialogFormValidData,
): void {
  if (props.isConfirmLoading) return;
  emit('confirmBtnClick', event, formData, props);
}

const mainEl = useTemplateRef('main');

function getSubmitBtn(): HTMLInputElement | null {
  if (!mainEl.value) return null;
  return mainEl.value.querySelector('input[type=submit]');
}

function handleConfirmClick(event: Event): void {
  const submitBtn = getSubmitBtn();
  if (!submitBtn) return;
  submitBtn.click();
}

const initialData = computed<NewQueryDialogFormInitialData>(() => {
  return {
    message: undefined,
  };
});

const _jobs = computed(() => {
  if (props.job) {
    return [
      {
        jobId: props.job.rockhopper_id,
        jobDate: props.job.rockhopper_date,
      },
    ];
  }
  return [];
});
</script>

<template>
  <PageDialogWrapper
    :class="$style.newQueryDialog"
    data-testid="newQueryDialog"
  >
    <template #header>
      <ReportGenerateDialogHeader @close-click="handleCancelClick">
        New query
      </ReportGenerateDialogHeader>
    </template>
    <section ref="main" :class="$style.main">
      <JobsBasicList :jobs="_jobs" class="mt-0 mb-6" />
      <div :class="$style.text">Write the message.</div>
      <NewQueryDialogForm
        :initial-data="initialData"
        :loading="props.isConfirmLoading"
        @submit="handleSubmit"
      />
    </section>
    <template #footer>
      <menu :class="$style.dialogFooterMenuList">
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            variant="outlined"
            append-icon="mdi-close"
            :class="$style.btn"
            @click="handleCancelClick"
          >
            Cancel
          </KBtn>
        </li>
        <li :class="$style.dialogFooterMenuListItem">
          <KBtn
            append-icon="mdi-plus"
            :class="$style.btn"
            :loading="props.isConfirmLoading"
            @click="handleConfirmClick"
          >
            Create
          </KBtn>
        </li>
      </menu>
    </template>
  </PageDialogWrapper>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use 'sass:math';
@use '@/styles/colors';
@use '@/styles/variables';

.newQueryDialog {
  width: variables.$reportGenerateDialogWidth;
}
  .main {
  }
  .text {
    margin: 0 0 math.div(variables.$pageDialogWindowMainWrapperMargin, 2) 0;
  }
  .dialogFooterMenuList {
    list-style: none;
    margin: 0;
    padding: 0;
    text-align: right;
  }
    .dialogFooterMenuListItem {
      display: inline;
    }
    .dialogFooterMenuListItem + .dialogFooterMenuListItem {
      margin-left: 1rem;
    }
      .btn {
      }
</style>
